import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CheckCircleIcon } from '../components/CheckCircleRow';
import { HomepageImageGlowWrap } from '../components/HomepageImageGlow';
import { homepageSectionImagePlaceholderStretchClassName } from '../constants/homepageImagePlaceholder';
import {
  homepageElevatedPanelClassName,
  homepagePrimaryButtonClassName,
  homepageSectionContainerClassName,
  homepageSectionPaddingYClassName,
  sectionHeadingAccentCenterClassName,
  sectionHeadingAccentClassName,
} from '../constants/homepageUi';

/** Secondary hero CTA — outline stays subdued until hover (homepage family). */
const businessHeroSecondaryButtonClassName =
  'inline-flex items-center justify-center rounded-lg border border-white/25 bg-transparent px-6 py-3 text-sm font-medium text-white/90 transition-all hover:border-[#00e3ff] hover:bg-[rgba(0,227,255,0.08)] hover:text-white hover:shadow-[0_0_14px_rgba(0,227,255,0.2)]';

/** Hero visual — full width of column, natural aspect ratio (same treatment as homepage hero). */
const businessHeroImageClassName =
  'w-full h-auto max-w-full rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]';

/** What We Offer split images — matches homepage Real-World block imagery. */
const businessOfferingBlockImageClassName =
  'w-full h-auto max-w-full rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]';

const VALUE_CARDS: { title: string; copy: string }[] = [
  {
    title: 'Existing Datasets',
    copy:
      'Start faster with structured real-world datasets across handwriting, household, kitchen, and food-condition categories.',
  },
  {
    title: 'Custom Collection',
    copy:
      'Launch targeted data capture for specific rooms, objects, conditions, edge cases, or workflows your model actually needs.',
  },
  {
    title: 'Annotation & QA',
    copy:
      'Use Pixano to support labeling, QA, transcription, and structured review workflows aligned to your project.',
  },
  {
    title: 'Delivery-Ready Output',
    copy:
      'Receive data in formats that plug into real training pipelines, including common CV export structures and custom schema support.',
  },
];

type OfferingBlock = {
  heading: string;
  body: string;
  bullets: string[];
  imageSide: 'left' | 'right';
  imageSrc?: string;
  imageAlt?: string;
};

const OFFERING_BLOCKS: OfferingBlock[] = [
  {
    heading: 'License Existing Real-World Datasets',
    body:
      'Access structured datasets collected in real environments — not synthetic scenes or stock-image filler. Pixano already supports categories across household, kitchen, handwriting, and food-condition data.',
    bullets: [
      'Faster model prototyping',
      'Fill training gaps',
      'Evaluate edge-case performance',
      'Reduce internal collection overhead',
    ],
    imageSide: 'left',
    imageSrc: '/assets/license-existing-business.png',
    imageAlt: 'Structured real-world datasets for licensing and model training',
  },
  {
    heading: 'Request Custom Data Collection',
    body:
      'When the right data does not exist yet, Pixano can create targeted capture workflows for your exact use case. We can scope environments, objects, conditions, and contributor instructions around the signals your model needs.',
    bullets: [
      'Specific objects and scenarios',
      'Real homes and real conditions',
      'Flexible task design',
      'Built around your collection goals',
    ],
    imageSide: 'right',
    imageSrc: '/assets/request-business.png',
    imageAlt: 'Custom real-world data collection scoped to your use case',
  },
  {
    heading: 'Annotation, QA, and Structured Review',
    body:
      'Pixano can support production labeling and review workflows across collection campaigns. From classification and QA to structured annotation support, the goal is to deliver cleaner, more usable data.',
    bullets: [
      'Human-reviewed workflows',
      'AI-assisted validation support',
      'Consistent schema alignment',
      'Better training-readiness',
    ],
    imageSide: 'left',
    imageSrc: '/assets/annotation-business.png',
    imageAlt: 'Annotation, QA, and structured review workflows for training data',
  },
  {
    heading: 'Delivery That Fits Your Pipeline',
    body:
      'We design outputs so your team can move faster once data is approved. That includes structured organization, exportable assets, and delivery aligned to the formats your stack expects.',
    bullets: [
      'Organized deliverables',
      'Export-friendly structure',
      'Flexible dataset packaging',
      'Easier integration into training workflows',
    ],
    imageSide: 'right',
    imageSrc: '/assets/delivery-business.png',
    imageAlt: 'Training-ready dataset delivery aligned to your pipeline',
  },
];

const WHY_PILLARS: { kicker: string; line: string }[] = [
  { kicker: 'Real-world capture', line: 'Real homes, real objects, real conditions.' },
  { kicker: 'Purpose-built coverage', line: 'Data aligned to actual model failure points and edge cases.' },
  { kicker: 'Structured workflows', line: 'Cleaner review, validation, and dataset organization.' },
  { kicker: 'Flexible sourcing', line: 'Existing data where possible, custom collection where needed.' },
];

const ForBusinesses: React.FC = () => {
  useEffect(() => {
    document.title = 'For Businesses — Pixano';
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <Navigation />
      <main>
        {/* 1. Hero */}
        <section className="bg-[#0B0F14] pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className={homepageSectionContainerClassName}>
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-12 lg:gap-14 xl:gap-16">
              <div className="w-full lg:w-[40%] lg:max-w-[40%] flex flex-col justify-center lg:pr-4">
                <h1 className="text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-8">
                  For Businesses & OEM Teams
                </h1>
                <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-10">
                  Pixano helps AI, robotics, and OEM teams source real-world training data without building a full
                  collection and annotation pipeline from scratch. License structured existing datasets, request custom
                  collection, or work with us on annotation and delivery aligned to your stack.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <a href="/request-dataset" className={homepagePrimaryButtonClassName}>
                    Request a Dataset
                  </a>
                  <Link to={{ pathname: '/', hash: 'training-data' }} className={businessHeroSecondaryButtonClassName}>
                    View Training Coverage
                  </Link>
                </div>
              </div>
              <div className="w-full lg:w-[60%] lg:max-w-[60%] flex justify-center lg:justify-end lg:items-center">
                <HomepageImageGlowWrap className="w-full max-w-full">
                  <img
                    src="/assets/hero-business.png"
                    alt="Teams sourcing real-world AI training data with Pixano"
                    className={businessHeroImageClassName}
                    decoding="async"
                  />
                </HomepageImageGlowWrap>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Business value */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
        >
          <div className={`${homepageSectionContainerClassName} flex flex-col items-center`}>
            <h2
              className={`text-center text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4 ${sectionHeadingAccentCenterClassName} inline-block`}
            >
              How Businesses Use Pixano
            </h2>
            <p className="mb-10 max-w-2xl text-center text-sm leading-relaxed text-white/60 sm:mb-11 sm:text-[15px]">
              One partner for licensed data, custom capture, review workflows, and exports that fit how your team trains
              models.
            </p>
            <div className="grid w-full max-w-[920px] grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
              {VALUE_CARDS.map((card) => (
                <div
                  key={card.title}
                  className={`${homepageElevatedPanelClassName} flex h-full flex-col p-5 transition-colors duration-200 sm:p-6 md:hover:border-[rgba(0,227,255,0.22)]`}
                >
                  <p className="text-[15px] font-semibold tracking-tight text-white sm:text-base">{card.title}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/60 sm:mt-3">{card.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. What we offer — alternating splits */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
        >
          <div className={homepageSectionContainerClassName}>
            <header className="mx-auto mb-12 max-w-2xl text-center lg:mb-14">
              <h2
                className={`text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.5rem] mb-4 ${sectionHeadingAccentCenterClassName} inline-block`}
              >
                What We Offer
              </h2>
              <p className="mx-auto max-w-xl text-[15px] leading-relaxed text-white/65 sm:text-base">
                From licensed datasets to custom collection, review, and delivery—built for production AI and robotics
                programs.
              </p>
            </header>

            <div className="flex flex-col gap-14 lg:gap-[4.25rem]">
              {OFFERING_BLOCKS.map((block) => {
                const rowAlign = block.imageSrc ? 'lg:items-center' : 'lg:items-stretch';
                const rowClasses =
                  block.imageSide === 'right'
                    ? `flex flex-col gap-9 lg:flex-row-reverse ${rowAlign} lg:gap-12 xl:gap-16`
                    : `flex flex-col gap-9 lg:flex-row ${rowAlign} lg:gap-12 xl:gap-16`;

                return (
                  <div key={block.heading} className={rowClasses}>
                    <div className="flex min-h-0 w-full min-w-0 flex-col self-stretch lg:flex-1 lg:basis-0">
                      <HomepageImageGlowWrap
                        className={block.imageSrc ? 'w-full max-w-full' : 'min-h-[420px] flex-1'}
                      >
                        {block.imageSrc ? (
                          <img
                            src={block.imageSrc}
                            alt={block.imageAlt ?? ''}
                            className={businessOfferingBlockImageClassName}
                            decoding="async"
                          />
                        ) : (
                          <div className={homepageSectionImagePlaceholderStretchClassName} aria-hidden />
                        )}
                      </HomepageImageGlowWrap>
                    </div>
                    <div className="flex w-full min-w-0 max-w-xl flex-col justify-center py-1 lg:max-w-[36rem] lg:flex-1 lg:basis-0 lg:py-2 xl:pr-4">
                      <h3
                        className={`mb-3.5 inline-block text-2xl font-bold tracking-tight text-white sm:mb-4 sm:text-3xl ${sectionHeadingAccentClassName}`}
                      >
                        {block.heading}
                      </h3>
                      <p className="mb-6 text-base leading-relaxed text-white/85 sm:text-lg">{block.body}</p>
                      <ul className="space-y-3.5 sm:space-y-4">
                        {block.bullets.map((item) => (
                          <li key={item} className="flex gap-3.5 sm:gap-4 items-start">
                            <CheckCircleIcon className="mt-0.5" />
                            <span className="text-base leading-relaxed text-white/90 sm:text-lg">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 4. Why buy from Pixano */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
        >
          <div className={`${homepageSectionContainerClassName} flex flex-col items-center`}>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
              Why teams choose PIXANO
            </p>
            <h2
              className={`text-center text-3xl font-bold tracking-tight text-white sm:text-4xl ${sectionHeadingAccentCenterClassName} mb-4 inline-block`}
            >
              Built for teams that need real-world data — not approximations
            </h2>
            <p className="mb-8 max-w-xl text-center text-sm leading-relaxed text-white/55 sm:mb-9 sm:text-[15px]">
              Pixano is designed for buyers who care about real environments, clear capture goals, and training-ready
              output.
            </p>
            <div className="grid w-full max-w-[920px] grid-cols-1 gap-3 sm:mb-0 md:grid-cols-2 md:gap-4">
              {WHY_PILLARS.map((item) => (
                <div
                  key={item.kicker}
                  className={`${homepageElevatedPanelClassName} flex h-full flex-col p-5 transition-colors duration-200 sm:p-6 md:hover:border-[rgba(0,227,255,0.22)]`}
                >
                  <p className="text-[15px] font-semibold tracking-tight text-white sm:text-base">{item.kicker}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/60 sm:mt-3">{item.line}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Final CTA */}
        <section
          className="scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] py-20 lg:py-28 xl:py-32"
          aria-labelledby="for-businesses-closing-cta-heading"
        >
          <div className={`${homepageSectionContainerClassName} flex flex-col items-center text-center`}>
            <h2
              id="for-businesses-closing-cta-heading"
              className={`mb-5 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:mb-6 sm:text-4xl lg:text-[2.25rem] ${sectionHeadingAccentCenterClassName} inline-block`}
            >
              Need existing data or a custom collection plan?
            </h2>
            <p className="mb-10 max-w-xl text-base leading-relaxed text-white/80 sm:mb-11 sm:text-lg">
              Tell us what you are building and we&apos;ll help match you with existing coverage or scope a custom
              dataset request.
            </p>
            <a href="/request-dataset" className={homepagePrimaryButtonClassName}>
              Request a Dataset
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ForBusinesses;
